import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import * as I from 'immutable';
import React from 'react';

import {ExploreRunPrompt} from 'app/components/AgentExplorer/types';
import {usePendingAgentRuns} from 'app/modules/AgentRuns/usePendingAgentRuns';
import {api} from 'app/modules/Remote';
import {AgentRunPlanParams, StartWorkflowRunOptions} from 'app/modules/Remote/api';
import {ApiFeature} from 'app/modules/Remote/Feature';

import {ApiAgentNote, ApiAgentRun, ApiAgentRunMetadata, ApiAgentWorkflow} from './types';

export const AGENT_RUN_DETAILS_QUERY_KEY = 'agent-run-details';

export const useAgentRunQuery = (workflowId: string, runId: string) => {
  return useQuery({
    queryKey: [AGENT_RUN_DETAILS_QUERY_KEY, workflowId, runId],
    queryFn: async () => {
      const response = await api.agent.getWorkflowRun(workflowId, runId);
      return response.toJS().data as ApiAgentRun;
    },
    enabled: !!runId,
  });
};

export const useAgentWorkflowRunsPaginatedQuery = (workflowId: string) => {
  const PER_PAGE = 25;
  const [currentPage, setCurrentPage] = React.useState(1);
  const [allRuns, setAllRuns] = React.useState<ApiAgentRunMetadata[] | undefined>(undefined);
  const [hasNextPage, setHasNextPage] = React.useState(true);

  const {data, isLoading, error} = useQuery({
    queryKey: ['agent-runs-paginated', workflowId, currentPage],
    queryFn: async () => {
      const response = await api.agent.getWorkflowRuns(workflowId, {
        perPage: PER_PAGE,
        page: currentPage,
      });
      return response.toJS();
    },
  });

  React.useEffect(() => {
    if (data) {
      if (currentPage > 1) {
        setAllRuns((prev) => (prev ? [...prev, ...data.data] : data.data));
      } else {
        setAllRuns(data.data);
      }
      setHasNextPage(data.data.length >= PER_PAGE);
    }
  }, [data, currentPage]);

  const loadNextPage = React.useCallback(() => {
    if (hasNextPage) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [hasNextPage]);

  // Start the accumulator over from page 1. A refetch of a later page would append it a second
  // time, so anything that changes the list server-side has to reset rather than invalidate.
  const reset = React.useCallback(() => {
    setCurrentPage(1);
    setAllRuns(undefined);
    setHasNextPage(true);
  }, []);

  return {
    runs: allRuns,
    isLoading,
    error,
    hasNextPage,
    loadNextPage,
    reset,
  };
};

export const useStartAgentWorkflowRun = (workflowId: string, workflowTitle?: string) => {
  const queryClient = useQueryClient();
  const {addPendingBulkRun} = usePendingAgentRuns();

  return useMutation({
    mutationFn: async (options?: StartWorkflowRunOptions) => {
      const response = await api.agent.startWorkflowRun(workflowId, options);
      return response.toJS().data as ApiAgentRun;
    },
    onSuccess: (run) => {
      addPendingBulkRun(Number(workflowId), run.id, workflowTitle);
      // Invalidate the runs list to show the new run
      queryClient.invalidateQueries({queryKey: ['agent-runs-paginated', workflowId]});
    },
  });
};

export const useArchiveAgentWorkflow = (organizationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (workflowId: string) => {
      const response = await api.agent.archiveWorkflow(workflowId);
      return response.toJS().data as ApiAgentWorkflow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['agent-workflows', organizationId]});
    },
  });
};

export const useArchiveAgentWorkflowRun = (workflowId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (runId: number) => {
      const response = await api.agent.archiveWorkflowRun(workflowId, runId);
      return response.toJS().data as ApiAgentRunMetadata;
    },
    onSuccess: () => {
      // Dropped rather than invalidated: the caller resets the accumulator back to page 1, and a
      // refetched page 2 would be appended twice.
      queryClient.removeQueries({queryKey: ['agent-runs-paginated', workflowId]});
    },
  });
};

// Create a workflow, optionally kicking off its first run. One mutation rather than two so the
// caller can't submit the run against a workflow id that a state update hasn't applied yet, and so
// a half-finished submit reports as a single failure.
export const useCreateAgentWorkflow = (organizationId: string) => {
  const queryClient = useQueryClient();
  const {addPendingBulkRun} = usePendingAgentRuns();

  return useMutation({
    mutationFn: async ({
      startRun,
      ...params
    }: AgentRunPlanParams & ExploreRunPrompt & {startRun: boolean}) => {
      const workflowResponse = await api.agent.makeWorkflow({organizationId, ...params});
      const workflow = workflowResponse.toJS().data as ApiAgentWorkflow;
      if (!startRun) {
        return {workflow, run: null};
      }
      // The run needs no options: it inherits the workflow's config, and the batch already fans out
      // into one AgentRun per property, each resolving its own scenes.
      const runResponse = await api.agent.startWorkflowRun(workflow.id);
      return {workflow, run: runResponse.toJS().data as ApiAgentRun};
    },
    onSuccess: ({workflow, run}) => {
      if (run) {
        addPendingBulkRun(Number(workflow.id), run.id, workflow.title);
      }
      queryClient.invalidateQueries({queryKey: ['agent-workflows', organizationId]});
    },
  });
};

// Directly call api to update agent notes instead of using the notes provider patchNote

// We get notes as part of the run response, not the notesActions.loadNotes, so using the
// notes provider patchNote is incompatible here.
export const useBatchUpdateNotes = (workflowId: string, runId: string) => {
  const queryClient = useQueryClient();
  // TODO: These can be numbers, remove when consolidated
  const wfKey = String(workflowId);
  const runKey = String(runId);

  return useMutation({
    mutationKey: ['update-notes-batch'],
    mutationFn: async (notes: ApiAgentNote[]) => {
      // patch all notes in parallel
      const results = await Promise.all(
        notes.map((note) =>
          api.featureCollections
            .features(note.featureCollectionId)
            .patchNote(note.projectId, note.featureId, note.id as number, note)
            .then((r) => r.toJS().data)
        )
      );
      return results as ApiAgentNote[];
    },
    onMutate: async (updatedNotes: ApiAgentNote[]) => {
      const queryKey = [AGENT_RUN_DETAILS_QUERY_KEY, wfKey, runKey];
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({queryKey});

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(queryKey);

      // Build a quick lookup for updated notes
      const updatedById = new Map(updatedNotes.map((n) => [n.id, n]));

      // Optimistically update the cache
      queryClient.setQueryData(queryKey, (old: ApiAgentRun | undefined) => {
        if (!old) return old;
        return {
          ...old,
          notes: old.notes.map((note) => updatedById.get(note.id) ?? note),
        };
      });

      return {previousData, queryKey};
    },
    onError: (_err, _updatedNotes, context) => {
      // Roll back to previous cache snapshot on error
      if (context?.previousData) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
    },
    onSettled: () => {
      // Invalidate to refetch and ensure server is source of truth
      queryClient.invalidateQueries({queryKey: [AGENT_RUN_DETAILS_QUERY_KEY, wfKey, runKey]});
    },
  });
};

// Using RQ here because there is a bug the context-org-id is not being set correctly on outgoing requests on load, so hacking in some default retry behavior.
export const useFeaturesQuery = (fcId: number) => {
  return useQuery({
    queryKey: ['agent-features', fcId],
    queryFn: async () => {
      const apiFeatures: I.ImmutableListOf<ApiFeature> = (
        await api.featureCollections.features(fcId).list({perPage: 200}, {getAllPages: true})
      ).get('data');
      return apiFeatures.toJS() as ApiFeature[];
    },
    retry: 3,
  });
};
